// components/Sidebar.js
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiHome, 
  FiUser, 
  FiAward, 
  FiSettings, 
  FiX,
  FiBarChart2,
  FiUsers,
  FiShoppingCart,
  FiDollarSign,
  FiActivity,
  FiLogOut, 
  FiChevronRight,
  FiZap,
  FiFlag
} from 'react-icons/fi';

const Sidebar = ({ isOpen, toggleSidebar, isMobile }) => {
  const location = useLocation();
  
  const menuItems = [
    { id: 1, name: 'Tableau de bord', icon: FiHome, path: '/dashboard', color: '#60a5fa' },
    { id: 6, name: 'Utilisateurs', icon: FiUsers, path: '/utilisateur', color: '#f87171' },
    { id: 7, name: 'Shop', icon: FiShoppingCart, path: '/shops', color: '#4ade80' },
    { id: 5, name: 'Article & Prix', icon: FiDollarSign, path: '/acticles', color: '#e879f9' },
    { id: 2, name: 'Analyse Vente IA', icon: FiActivity, path: '/StatsIa', color: '#38bdf8' },
    { id: 4, name: 'Statistiques Shops', icon: FiBarChart2, path: '/statistiques', color: '#fbbf24' },
    { id: 3, name: 'Top Liste', icon: FiAward, path: '/top10', color: '#34d399' },
    { id: 9, name: 'Evenements', icon: FiFlag, path: '/Events', color: '#e811ab' },
    { id: 8, name: 'Paramètres', icon: FiSettings, path: '/settings', color: '#94a3b8' },
  ];

  const handleMenuItemClick = () => {
    if (isMobile) {
      toggleSidebar();
    }
  };

  // Variantes pour l'animation mobile
  const sidebarVariants = {
    open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } },
    closed: { x: '-100%', transition: { type: 'spring', stiffness: 300, damping: 30 } }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({ 
      opacity: 1, 
      x: 0, 
      transition: { delay: i * 0.05, type: 'spring', stiffness: 200, damping: 20 } 
    })
  };

  // Gestion de l'animation selon le mode
  const getSidebarAnimation = () => {
    if (isMobile) {
      // En mobile : animation selon isOpen
      return isOpen ? 'open' : 'closed';
    }
    // En desktop : toujours ouvert, pas d'animation initiale de slide
    return 'open';
  };

  return (
    <>
      {/* Overlay pour mobile avec glassmorphism */}
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={toggleSidebar}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-40"
          />
        )}
      </AnimatePresence>

      {/* Sidebar principal */}
      <motion.aside
        variants={sidebarVariants}
        initial={isMobile ? 'closed' : false} // desktop : pas d'animation initiale
        animate={getSidebarAnimation()}
        className={`
          fixed md:sticky top-0 left-0 h-screen
          w-[280px] max-w-[85vw] md:w-[280px]
          bg-gradient-to-b from-blue-950/90 via-indigo-950/85 to-slate-900/90
          backdrop-blur-xl
          border-r border-blue-400/20
          shadow-2xl shadow-blue-500/10
          flex flex-col
          z-50
          overflow-hidden
        `}
      >
        {/* Particules décoratives */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 -right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 w-60 h-60 bg-cyan-500/5 rounded-full blur-3xl" />
        </div>

        {/* En-tête */}
        <div className="relative px-5 py-6 border-b border-white/[0.06] flex items-center justify-between">
          <Link to="/dashboard" onClick={handleMenuItemClick} className="flex items-center gap-3 group">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-400/20 group-hover:scale-110 transition-transform duration-300">
              <FiZap className="text-blue-400 text-xl" />
            </div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              RefisC-RDC
            </h2>
          </Link>
          
          {isMobile && (
            <motion.button
              onClick={toggleSidebar}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              className="p-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all md:hidden"
            >
              <FiX size={18} />
            </motion.button>
          )}
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 px-3 py-6 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-500/20 scrollbar-track-transparent">
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <motion.div
                key={item.id}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={itemVariants}
              >
                <Link
                  to={item.path}
                  onClick={handleMenuItemClick}
                  className={`
                    group flex items-center gap-3 px-4 py-3 rounded-2xl
                    transition-all duration-300 relative overflow-hidden
                    ${isActive 
                      ? 'bg-gradient-to-r from-blue-600/20 to-blue-500/10 border border-blue-400/30 shadow-lg shadow-blue-500/10' 
                      : 'hover:bg-white/[0.03] border border-transparent hover:border-white/[0.06]'
                    }
                  `}
                >
                  {/* Indicateur actif */}
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full bg-gradient-to-b from-blue-400 to-cyan-400"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                  
                  <span 
                    className="flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-300"
                    style={{ 
                      backgroundColor: `${item.color}15`,
                      color: item.color,
                      boxShadow: isActive ? `0 0 12px ${item.color}40` : 'none'
                    }}
                  >
                    <item.icon size={20} />
                  </span>
                  
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    isActive ? 'text-white' : 'text-slate-300 group-hover:text-white'
                  }`}>
                    {item.name}
                  </span>
                  
                  {isActive && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="ml-auto"
                    >
                      <FiChevronRight className="text-blue-400" size={16} />
                    </motion.div>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </nav>

        {/* Footer utilisateur */}
        <div className="relative p-4 border-t border-white/[0.06] space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] transition-all">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <FiUser size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">REFISC</p>
              <p className="text-xs text-slate-400">Administrateur</p>
            </div>
          </div>
          
          <motion.button
            onClick={() => {
              localStorage.removeItem('login2');
              window.location.href = '/login';
            }}
            whileHover={{ x: 5 }}
            whileTap={{ scale: 0.98 }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-all text-sm font-medium"
          >
            <FiLogOut size={16} />
            Déconnexion
          </motion.button>
        </div>

        {/* Version */}
        <div className="relative px-5 py-3 border-t border-white/[0.06] text-center">
          <span className="text-[10px] text-slate-500">v1.0.0</span>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;